import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";

const SLIDES = [
  {
    img: "/slide-chocolate.jpg", alt: "Luxury chocolate truffles and florentines served on a porcelain plate",
    eyebrow: "Luxury chocolate", title: <>Indulgent chocolate, <em className="text-[#F3D98B] font-medium">hand-picked</em> for you</>,
    text: "Truffles, florentines and fine Belgian chocolate from Britain's best food halls, posted anywhere in the UK.",
    cta: "Shop chocolate", to: "#shop",
  },
  {
    img: "/slide-biscuits-tea.jpg", alt: "Shortbread, chocolate biscuits and gingerbread with a cup of tea",
    eyebrow: "Biscuits & tea", title: <>Proper British <em className="text-[#F3D98B] font-medium">tea time</em></>,
    text: "Buttery shortbread, chocolate biscuits and festive gingerbread — the perfect partner to a good cuppa.",
    cta: "Shop biscuits", to: "#shop",
  },
  {
    img: "/slide-snacks.jpg", alt: "Fudge, chocolate nuts, fruit jellies and crackers in bowls beside a hamper",
    eyebrow: "Sweets, snacks & hampers", title: <>Treats worth <em className="text-[#F3D98B] font-medium">sharing</em></>,
    text: "Fudge, chocolate nuts, sweets and savoury nibbles — or build a gift hamper with their favourites.",
    cta: "Build a hamper", to: "/hampers",
  },
];
const DELAY = 4500;

const Cta = ({ s }) => {
  const cls = "mt-8 inline-flex items-center gap-2 rounded-full bg-[#F3D98B] text-[#0F172A] px-7 py-3.5 font-semibold hover:bg-white transition-colors";
  return s.to.startsWith("#")
    ? <a href={s.to} className={cls} data-testid="hero-shop-button"><ShoppingBag className="w-4 h-4" /> {s.cta}</a>
    : <Link to={s.to} className={cls} data-testid="hero-shop-button"><ShoppingBag className="w-4 h-4" /> {s.cta}</Link>;
};

export default function HeroSlider() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((n) => setI((n + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => go(i + 1), DELAY);
    return () => clearTimeout(t);
  }, [i, paused, go]);

  return (
    <section className="relative h-[34rem] md:h-[38rem] overflow-hidden bg-[#1E2A44] text-white" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} aria-roledescription="carousel" data-testid="hero-slider">
      {SLIDES.map((s, k) => (
        <div key={k} className={`absolute inset-0 transition-opacity duration-1000 ${k === i ? "opacity-100 z-10" : "opacity-0 z-0"}`} aria-hidden={k !== i} data-testid={`hero-slide-${k}`}>
          <img src={s.img} alt={s.alt} className={`absolute inset-0 w-full h-full object-cover object-right transition-transform duration-[6000ms] ease-out ${k === i ? "scale-105" : "scale-100"}`} loading={k === 0 ? "eager" : "lazy"} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/60 to-transparent" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            {k === i && (
              <div className="max-w-xl rise">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F3D98B]">{s.eyebrow}</p>
                <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">{s.title}</h1>
                <p className="mt-6 text-base md:text-lg text-white/85">{s.text}</p>
                <Cta s={s} />
              </div>
            )}
          </div>
        </div>
      ))}
      <button onClick={() => go(i - 1)} aria-label="Previous slide" className="absolute z-20 left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0F172A]/55 backdrop-blur-md border border-white/40 grid place-items-center hover:bg-[#0F172A]/80 transition-colors" data-testid="hero-prev"><ChevronLeft className="w-5 h-5" /></button>
      <button onClick={() => go(i + 1)} aria-label="Next slide" className="absolute z-20 right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0F172A]/55 backdrop-blur-md border border-white/40 grid place-items-center hover:bg-[#0F172A]/80 transition-colors" data-testid="hero-next"><ChevronRight className="w-5 h-5" /></button>
      <div className="absolute z-20 bottom-6 left-1/2 -translate-x-1/2 flex gap-2" data-testid="hero-dots">
        {SLIDES.map((_, k) => (
          <button key={k} onClick={() => go(k)} aria-label={`Go to slide ${k + 1}`} aria-current={k === i} className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${k === i ? "w-8 bg-[#F3D98B]" : "w-2.5 bg-white/50 hover:bg-white"}`} data-testid={`hero-dot-${k}`} />
        ))}
      </div>
    </section>
  );
}
